import { useState } from "react";

interface FilterGroup {
  key: "region" | "year" | "genre";
  label: string;
  options: string[];
}

interface FilterBarProps {
  groups: FilterGroup[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  sort: string;
  onSortChange: (sort: string) => void;
}

const sortOptions = [
  { key: "latest", label: "最新", icon: "ri-time-line" },
  { key: "hot", label: "最热", icon: "ri-fire-line" },
  { key: "rating", label: "评分", icon: "ri-star-line" },
];

export default function FilterBar({ groups, values, onChange, sort, onSortChange }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const renderGroup = (group: FilterGroup) => (
    <div className="flex flex-col gap-2 md:flex-row md:items-start">
      <span className="flex items-center gap-1.5 shrink-0 pt-0.5 text-sm font-label font-medium text-foreground-500 md:w-14">
        {group.label}
      </span>
      <div className="flex flex-wrap gap-2">
        {group.options.map((option) => {
          const active = (values[group.key] || "全部") === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(group.key, option)}
              className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="rounded-lg bg-background-50 border border-background-200/70 p-4 md:p-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-foreground-700">
          <i className="ri-filter-3-line text-primary-500" />
          <span className="font-label font-medium">筛选与排序</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1 bg-background-100 p-1 rounded-full">
            {sortOptions.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onSortChange(item.key)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-label whitespace-nowrap transition-colors ${
                  sort === item.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className={item.icon} /> {item.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-background-100 text-sm text-foreground-700 whitespace-nowrap"
          >
            <i className="ri-equalizer-line" /> 筛选
          </button>
        </div>
      </div>

      <div className={`${mobileOpen ? "flex" : "hidden"} md:flex flex-col gap-4 mt-4 pt-4 border-t border-background-200/70`}>
        <div className="flex sm:hidden items-center gap-1 bg-background-100 p-1 rounded-full w-fit flex-wrap">
          {sortOptions.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onSortChange(item.key)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-label whitespace-nowrap transition-colors ${
                sort === item.key ? "bg-primary-500 text-background-50" : "text-foreground-600"
              }`}
            >
              <i className={item.icon} /> {item.label}
            </button>
          ))}
        </div>
        {groups.map((group) => (
          <div key={group.key}>{renderGroup(group)}</div>
        ))}
      </div>
    </div>
  );
}